import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    paymentId: string;
    onSuccess: () => void;
}

const PaymentReversalDialog: React.FC<Props> = ({ open, onClose, paymentId, onSuccess }) => {
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const handleSubmit = async () => {
        if (!reason.trim()) {
            showApiError(new Error('Reason required'), t('paymentReversal.toast.reasonRequired'));
            return;
        }
        setSubmitting(true);
        try {
            await PayrollService.reversePayment(paymentId, { reason: reason.trim() });
            showSuccess(t('paymentReversal.toast.reversed'));
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, t('paymentReversal.toast.failed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('paymentReversal.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        label={t('common:fields.reason')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        autoFocus
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    disabled={submitting || !reason.trim()}
                >
                    {submitting ? t('paymentReversal.reversing') : t('shared.reverse')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentReversalDialog;
